<template>
    <div v-if="!deletedRoom">
        <b-row class="sticky-top mb-2">
            <b-col>
                <b-row align-v="center">
                    <b-col cols="8" class="sticky-row">
                            <div v-if="(gridItems && gridItems.length > 0) || breadcrumb.length > 1">
                                <span v-for="(node, index) in breadcrumb" :key="node.id" class="lead">
                                    <span v-if="index > 0" class="mr-1 ml-1">></span>
                                    <b-link @click="goToBreadcrumbLevel(index)">{{ node.title }}</b-link>
                                </span>
                            </div>
                    </b-col>
                    <b-col cols="4" align-self="center"></b-col>
                </b-row>
            </b-col>
        </b-row>
        <b-row>
            <b-col v-if="splitterPercent">
                <vue-splitter @resize="onSplitterReize()"
                              :margin="35" 
                              :defaultPercent="splitterPercent" 
                              ref="splitter">
                    <div slot="left-pane">
                        <ag-grid-vue
                            class="ag-theme-balham"
                            style="height: 79vh"
                            :enableColResize="true"
                            :enableSorting="true"
                            :suppressCellSelection="true"
                            rowSelection="multiple"
                            :localeText="localeText"
                            :columnDefs="columnDefs"
                            :rowData="gridItems"
                            :gridReady="onGridReady"
                            :rowClicked="onRowClicked"
                            :rowDoubleClicked="onRowDoubleClicked"
                            :getRowNodeId="provideRowNodeId"
                            :postSort="onPostSort"
                            :frameworkComponents="frameworkComponents"
                            :statusBar="gridStatusBar"
                            :rowSelected="onRowSelected"
                            :isRowSelectable="isRowSelectable"
                            :suppressRowClickSelection="true">
                        </ag-grid-vue>

                        <upload-file-modal :root="rootNodeForUpload.id" 
                                           :successCallback="uploadSuccess"
                                           :file-revision-info="fileRevisionInfo">
                        </upload-file-modal>

                        <!-- Action modals -->
                        <b-modal v-model="showCreateTaskModal"
                                :title="$t('files.context.createtask')"
                                header-bg-variant="secondary"
                                header-text-variant="white"
                                :ok-title="$t('tasks.addTask')"
                                ok-variant="outline-info"
                                :cancel-title="$t('general.cancel')"
                                cancel-variant="outline-secondary"
                                centered
                                no-fade
                                no-close-on-esc
                                no-close-on-backdrop
                                hide-header-close
                                @shown="onCreateTaskModalShown"
                                @ok.prevent="addTask">
                            <file-create-task-modal :file="selectedFile" @closeCreateTaskModal="onCloseCreateTaskModal" ref="refCreateTaskModal"></file-create-task-modal>
                        </b-modal>

                        <b-modal v-model="showMessageForm"
                                :title="$t('feed.newMessage.name')"
                                header-bg-variant="secondary"
                                header-text-variant="white"
                                :ok-title="$t('feed.newMessage.create')"
                                ok-variant="outline-info"
                                :cancel-title="$t('general.cancel')"
                                cancel-variant="outline-secondary"
                                centered
                                no-fade
                                no-close-on-esc
                                no-close-on-backdrop
                                hide-header-close
                                @ok.prevent="createNewMessage"
                                @shown="onNewMessageModalShown">
                            <new-message-form :file="selectedFile" :hide-button="true" @closeMessageForm="onCloseMessageForm" ref="newMessageModal"></new-message-form>
                        </b-modal>

                        <b-modal v-model="showShareModal"
                                :title="$t('share.title')"
                                header-bg-variant="secondary"
                                header-text-variant="white"
                                ok-title="Send"
                                ok-variant="outline-info"
                                :cancel-title="$t('general.cancel')"
                                cancel-variant="outline-secondary"
                                centered
                                no-fade
                                no-close-on-esc
                                no-close-on-backdrop
                                hide-header-close
                                hide-footer>
                            <file-share-modal :file="selectedFile" @closeShareModal="onCloseShareModal"></file-share-modal>
                        </b-modal>
                    </div>
                    <div slot="right-pane">
                        <div style="border: 1px solid lightgray;">
                            <div style="height: 78.7vh;" v-if="$refs.splitter">
                                <div v-show="isSplitterActive" class="preview-text">
                                    <small>{{ $t('project.settings.files.previewAfterResizing') }}</small>
                                </div>
                                <iframe
                                    v-show="!isSplitterActive"
                                    id="iframeViewer"
                                    ref="iframeViewerRef"
                                    :src="iframeViewerUrl"
                                    frameborder="0"
                                    allowfullscreen
                                    width="100%"
                                    height="100%">
                                </iframe>
                            </div>
                        </div>
                    </div>
                </vue-splitter>
            </b-col>
        </b-row>
    </div>
    <div v-else-if="deletedRoom">
        <b-alert show variant="warning">
            <b-media no-body>
                <b-media-aside vertical-align="center">
                    <icon name="warning" scale="3"></icon>
                </b-media-aside>
                <b-media-body class="ml-3">
                    <p class="m-0">{{ $t('project.settings.files.noFilesDeletedRoom') }}</p>
                </b-media-body>
            </b-media>
        </b-alert>
    </div>
</template>

<script>
import projectsApi from '../../../api/projects';
import userDataApi from '../../../api/userData';
import { AgGridVue } from 'ag-grid-vue';
import { mapGetters } from 'vuex';
import moment from 'moment';
import i18n from '@/i18n.bootstrap';
import toastr from 'toastr';
import VueSplitter from '@rmp135/vue-splitter';

import TitleCellRendererComponent from './TitleCellRendererComponent';
import ActionsCellRendererComponent from './ActionsCellRendererComponent';
import QuickFilterStatusBarComponent from './QuickFilterStatusBarComponent.js';

import FileCreateTaskModal from './FileCreateTaskModal';
import NewMessageForm from '../../Shared/NewMessageForm';
import FileShareModal from '../Files/FileShareModal';
import UploadFileModal from './UploadFileModal';
import Utils from '../../../Utils';

export default {
    components: {
        AgGridVue,
        UploadFileModal,
        FileCreateTaskModal,
        NewMessageForm,
        FileShareModal,
        VueSplitter
    },

    data() {
        return {
            mainNode: undefined,
            breadcrumb: [],
            gridItems: undefined,
            projectId: this.$route.params.projectId,

            columnDefs: null,
            gridStatusBar: null,
            gridApi: null,
            columnApi: null,

            showCreateTaskModal: false,
            showMessageForm: false,
            showShareModal: false,
            selectedFile: null,
            selectedFilesArray: [],

            splitterPercent: undefined,
            splitterState: false,

            isUploadModalInEditMode: undefined,

            localeText: {
                noRowsToShow: i18n.t('project.settings.files.noFiles'),
                loadingOoo: i18n.t('project.settings.files.loadingMessage'),
                filteredRowCount: i18n.t('project.settings.files.filteredRowCount'),
                rowCount: i18n.t('project.settings.files.totalRowCount'),

                errorLoadingFiles: i18n.t('project.settings.files.errorLoadingFiles')
            },

            iframeViewerUrl: undefined,
            fileRevisionInfo: null,
            deletedRoom: false
        }
    },
    
    beforeRouteEnter (to, from, next) {
        userDataApi.getUserSettings()
            .then(res => { next(vm => vm.splitterPercent = parseFloat(res.data.splitterPosition) || 50) });
    },

    created() {
        this.$store.dispatch('projects/loadTaskLists', { projectId: this.projectId });
        this.$store.dispatch('projects/getProjectUploadStatus', { projectId: this.projectId });

        this.$root.$on('file-actions::open-create-task-modal', (payload) => {
            this.selectedFile = this.gridItems.find(file => file.id === payload.fileId);
            this.showCreateTaskModal = true;
        });

        this.$root.$on('file-actions::open-create-message-modal', (payload) => {
            this.selectedFile = this.gridItems.find(file => file.id === payload.fileId);
            this.showMessageForm = true;
        });

        this.$root.$on('file-actions::open-share-modal', (payload) => {
            this.selectedFile = this.gridItems.find(file => file.id === payload.fileId);
            this.showShareModal = true;
        });

        // this.$root.$on('file-actions::open-edit-file-modal', (payload) => {
        //     this.openFileUploadDialog(payload.file, true);
        // });

        this.$root.$on('file-actions::open-rename-file-modal', (payload) => {
            this.openFileUploadDialog(payload.file, true, false);
        });

        this.$root.$on('file-actions::open-replace-file-modal', (payload) => {
            this.openFileUploadDialog(payload.file, false, true);
        });

        this.$root.$on('file-grid-status-bar::upload-btn-clicked', () => {
            this.openFileUploadDialog(null, false);
        });

        this.$root.$on('file-grid-status-bar::download-btn-clicked', () => {
            this.downloadSelectedFiles();
        });
        
        this.$root.$on('file-actions::file-deleted', (file) => {
            if (file.fileId) {
                this.highlightFirstDocument(this.gridItems);
            }
        });
    },

    beforeMount() {
        this.columnDefs = [
            {
                checkboxSelection: true,
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
                width: 25
            },
            { 
                headerName: i18n.t('files.title'), 
                field: 'title',  
                menuTabs: [], 
                cellRenderer: 'titleCellRendererComponent', 
                width: 150 
            },
            {
                headerName: i18n.t('files.info'), 
                field: 'info',  
                menuTabs: [], 
                width: 150 
            },
            { 
                headerName: i18n.t('files.modified'), 
                field: 'modified',  
                menuTabs: [], 
                valueFormatter: this.dateFormatter, 
                suppressSizeToFit: true, 
                width: 100,
                getQuickFilterText: (params) => {
                    return moment(params.data.modified).format('DD.MM.YYYY');
                }
            },
            { 
                headerName: i18n.t('files.actions'),  
                menuTabs: [], 
                cellRenderer: 'actionsCellRendererComponent', 
                suppressSorting: true, 
                suppressSizeToFit: true, 
                width: this.isGuestUser ? 125 : 290
            }
        ];

        this.frameworkComponents = { 
            titleCellRendererComponent: TitleCellRendererComponent,
            actionsCellRendererComponent: ActionsCellRendererComponent,
            quickFilterStatusBarComponent: QuickFilterStatusBarComponent
        };

        this.gridStatusBar = {
            statusPanels: [
                { statusPanel: 'quickFilterStatusBarComponent', align: 'left' },
                { statusPanel: 'agFilteredRowCountComponent' },
                { statusPanel: 'agTotalRowCountComponent' }
            ]
        };
    },

    mounted() {
        this.showFiles();

        // refresh project collaborators because 'isGuestUser'
        // computed prop depends on them
        // TODO: fix 'isGuestUser' in the future not to depend on project collaborators
        this.getProjectCollaborators();

        window.onresize = () => {
            this.gridApi.sizeColumnsToFit();
        }
    },

    computed: {
        ...mapGetters({
            isGuestUser: 'projects/isGuestUser',
            isUploadAvailable: 'projects/isColabTypeAvailable',
            globalConfig: 'system/globalConfig',
            projectData: 'projects/projectData'
        }),

        rootNodeForUpload() {
            if (!this.breadcrumb || this.breadcrumb.length === 0) {
                return { id: -1 };
            } else {
                return this.breadcrumb[this.breadcrumb.length - 1];
            }
        },

        isSplitterActive() {
            return this.$refs.splitter.active;
        },

        splitterCurrentPercent() {
            return { currentPosition: this.$refs.splitter.percent }
        }
    },

    beforeDestroy() {
      this.$store.dispatch('projects/setNumberOfSelectedFiles', 0);
      this.$root.$off('file-grid-status-bar::download-btn-clicked');
    },

    methods: {
        onGridReady(params) {
            this.gridApi = params.api;
            this.columnApi = params.columnApi;

            this.clearQuickSearch();

            this.applyGridDefaultSort();

            this.gridApi.sizeColumnsToFit();

            this.setSplitterOnChange();

            // remove horizontal scroll-bar from ag-grid when folder is present
            let grid = document.querySelector('.ag-body-viewport.ag-layout-normal');
            grid.style.overflowX = 'hidden';
        },

        setSplitterOnChange() {
            let that = this;
            let { splitter } = this.$refs;
            splitter.$el.childNodes.forEach(function(item, index) {
                if(index % 2===0) {
                    item.onmouseup = () => {
                        that.saveSplitterPercent();
                    }
                }
            });
        },

        onSplitterReize() {
            this.gridApi.sizeColumnsToFit();
        }, 

        saveSplitterPercent() {
            if(this.isSplitterActive) {
                userDataApi.setSplitterPercent(this.splitterCurrentPercent)
                    .then(res => this.splitterPercent = parseFloat(res.data.splitterPosition))
                    .catch(err => console.log(err));
            }
        },

        onRowClicked(params) {
            this.selectedFile = params.data;
            if (this.selectedFile.type.toUpperCase() === 'DOCUMENT') {
                this.selectGridItem(params.data);
            } else {
                this.setDocViewerSrc();
            }
        },

        onRowDoubleClicked(params) {
            this.selectedFile = params.data;
            if (this.selectedFile.type.toUpperCase() !== 'DOCUMENT') {
                // disable download files button
                this.gridApi.deselectAll();

                // check depth
                const queryDepth = this.projectData.documentQueryDepth;
                if (this.selectedFile.depth > queryDepth) {
                    toastr.info(i18n.t('files.queryDepthWarning'));
                } else {
                    this.selectGridItem(params.data);
                }
            }
        },

        // eslint-disable-next-line
        onRowSelected(event) {
            // event.node.data is actual selected/deselected node data
            let selectedRows = this.gridApi.getSelectedRows();
            this.selectedFilesArray = Utils.collectFilesDataRec(selectedRows);
            this.$store.dispatch('projects/setNumberOfSelectedFiles', this.selectedFilesArray.length);
        },

        isRowSelectable(rowNode) {
            let node = rowNode.data;
            return ((node.type.toUpperCase() === ('REGISTER' || 'FOLDER') && node.children.length > 0) || 
                   (node.type.toUpperCase() === 'DOCUMENT')) && node.acl.downloadable;
        },

        downloadSelectedFiles() {
            if(this.selectedFilesArray.length === 0) return false;

            this.$store.dispatch('system/setDownloadingStatus', true);

            let selectedFilesObj = { selectedFiles: this.selectedFilesArray };
            projectsApi.sendFilesForZip(this.projectId, selectedFilesObj)
                .then(res => {
                    this.$store.dispatch('system/setDownloadingStatus', false);

                    // start download
                    let {archiveHash} = res.data;
                    let url = `/colab/epr/projects/${this.projectId}/multiple/download/zip/${archiveHash}`;
                    window.location.href = url;
                    
                    // deselect all checkboxes
                    this.gridApi.deselectAll();

                    this.highlightFirstDocument(this.gridItems);
                })
                .catch(() => {
                    this.$store.dispatch('system/setDownloadingStatus', false);
                    toastr.error(i18n.t('project.settings.files.errorDownloadingFiles'));
                });
        },

        onPostSort(rowNodes) {
            // move folders to top after sort
            let move = (toIndex, fromIndex) => {
                rowNodes.splice(toIndex, 0, rowNodes.splice(fromIndex, 1)[0]);
            };
            
            let folderInsertIndex = 0;
            for (let i = 0; i < rowNodes.length; i++) {
                if (rowNodes[i].data.type.toUpperCase() !== 'DOCUMENT') {
                    move(folderInsertIndex++, i);
                }
            }

            // highligh and preview the first document after sort
            if(this.$route.query.displayFileId !== undefined) {
                for (let i = 0; i < rowNodes.length; i++) {
                    if (rowNodes[i].data.type.toUpperCase() === 'DOCUMENT') {
                        this.highlightNodeById(rowNodes[i].id, false);
                        break;
                    }
                }
            }
        },

        provideRowNodeId(data) {
            return data.id;
        },

        applyGridDefaultSort() {
            let sortModel = [{
                colId: 'title',
                sort: 'asc'
            }];
            this.gridApi.setSortModel(sortModel);
        },

        dateFormatter(params) {
            return moment(params.value).format('DD.MM.YYYY');
        },

        clearQuickSearch() {
            this.$root.$emit('file-grid-status-bar::clear-quick-filter');
        },

        onCloseCreateTaskModal() {
            this.showCreateTaskModal = false;
        },

        onCreateTaskModalShown() {
            this.$refs.refCreateTaskModal.clearForm();
            this.$refs.refCreateTaskModal.$validator.reset();
        },

        addTask() {
            this.$refs.refCreateTaskModal.addTask();
        },

        createNewMessage() {
            this.$refs.newMessageModal.createNewMessage();
        },

        onNewMessageModalShown() {
            this.$refs.newMessageModal.clearMessageForm();
            this.$refs.newMessageModal.$validator.reset();
        },

        onCloseMessageForm() {
            this.showMessageForm = false;
        },

        onCloseShareModal() {
            this.showShareModal = false;
        },

        showFiles() {
            this.getFilesFromECM().then(response => {
                if (response && response.children) {
                    response.depth = 1;
                    this.addFolderDepth(response.children, 2);

                    this.mainNode = response;
                    if (this.$route.query.displayFileId) {
                        this.previewFileById(this.$route.query.displayFileId);
                    } else {
                        let nodeList = this.processPathIndex();
                        if (!nodeList) {
                            nodeList = this.mainNode.children;
                        }
                        
                        this.highlightFirstDocument(nodeList);
                    }
                } else {
                    this.mainNode = undefined;
                    this.gridItems = undefined;
                    this.deletedRoom = true;
                }
            }).catch((error) => {
                console.log('Error loading files', error);
                //toastr.error(this.localeText.errorLoadingFiles);
                this.mainNode = undefined;
                this.gridItems = undefined;
                if (error.response.data.type === 'FOLDER_OR_REGISTER_NOT_AVAILABLE') {
                    console.log('Error loading files', error.response.data.type);
                    this.deletedRoom = true;
                }
            });
        },

        addFolderDepth(nodes, depth) {
            for (let i = 0; i <= nodes.length - 1; i++) {
                const node = nodes[i];
                if (node.type.toUpperCase() === 'REGISTER' || node.type.toUpperCase() === 'FOLDER') {
                    node.depth = depth;
                }

                if (node.children && node.children.length > 0) {
                    this.addFolderDepth(node.children, depth + 1);
                }
            }
        },

        previewFileById(id) {
            let pathIndex = this.getPathIndexByFileId(this.mainNode, id);

            if (!pathIndex) {
                pathIndex = [this.mainNode.id];
            } else {
                pathIndex.unshift(this.mainNode.id);
            }

            this.processPathIndex(pathIndex.join());
            this.highlightNodeById(id);
            
            // fix url to contain pathIndex only
            this.$router.replace({ query: { pathIndex: pathIndex.join() } });
        },

        processPathIndex(inputPathIndex) {
            let pathIndex = inputPathIndex || this.$route.query.pathIndex;
            if (!pathIndex) {
                // no path index => show root folder
                this.gridItems = this.mainNode.children;
                this.setBreadcrumb('APPEND', [this.mainNode]);
                this.setPathIndexUrl('APPEND', [this.mainNode.id]);
            } else {
                // path index not empty => show files according to path index
                let pathIndexParts = pathIndex.split(',');
                
                let nodeList = [];
                pathIndexParts.forEach(nodeId => {
                    let foundNode = this.getNodeById(this.mainNode, nodeId);
                    if (foundNode) {
                        nodeList.push(foundNode);
                    }
                });

                this.setBreadcrumb('APPEND', nodeList);
                this.gridItems = nodeList[nodeList.length - 1].children;
                return nodeList[nodeList.length - 1].children;
            }
        },

        getPathIndexByFileId(node, id) {
            if (node.id === id) {
                return [];
            } else if (node.children != null) {
                var foundNode = null;
                for(let i = 0; foundNode == null && i < node.children.length; i++) {
                    var path = this.getPathIndexByFileId(node.children[i], id);
                    if (path != null) {
                        if (node.children[i].type.toUpperCase() !== 'DOCUMENT') {
                            path.unshift(node.children[i].id);
                        }
                        return path;
                    }
                }
            }
            return null;
        },

        setPathIndexUrl(actionType, nodeIdList) {
            let pathIndexParts = !this.$route.query.pathIndex ? [] : this.$route.query.pathIndex.split(',');

            if (actionType.toUpperCase() === 'APPEND') {
                nodeIdList.forEach(nodeId => {
                    pathIndexParts.push(nodeId);
                });
            }

            if (actionType.toUpperCase() === 'REMOVE') {
                nodeIdList.forEach(nodeId => {
                    let idx = pathIndexParts.indexOf(nodeId);
                    if (idx > -1) {
                        pathIndexParts.splice(idx, 1);
                    }
                });
            }
            
            this.$router.replace({ query: { pathIndex: pathIndexParts.join(',') } });
        },

        setBreadcrumb(actionType, nodeList) {
            this.breadcrumb = this.breadcrumb || [];

            if (actionType.toUpperCase() === 'APPEND') {
                nodeList.forEach(node => {
                    if (!node.title) {
                        node.title = i18n.t('project.settings.files.noNameFolderTitle');
                    }
                    this.breadcrumb.push(node);
                });
            }

            if (actionType.toUpperCase() === 'REMOVE') {
                nodeList.forEach(node => {
                    if (node.title === i18n.t('project.settings.files.noNameFolderTitle')) {
                        node.title = '';
                    }

                    let nodeIdList = this.breadcrumb.map(bc => bc.id);
                    let idx = nodeIdList.indexOf(node.id);
                    if (idx > -1) {
                        this.breadcrumb.splice(idx, 1);
                    }
                });
            }
        },

        goToBreadcrumbLevel(index) {
            this.gridItems = this.breadcrumb[index].children;

            let nodesToRemove = this.breadcrumb.slice(index + 1);
            this.setBreadcrumb('REMOVE', nodesToRemove);
            this.setPathIndexUrl('REMOVE', nodesToRemove.map(n => n.id));

            // reset doc viewer
            this.setDocViewerSrc();

            this.highlightFirstDocument(this.breadcrumb[index].children);

            // disable download files button
            this.gridApi.deselectAll();
        },

        selectGridItem(item) {
            if (item.type.toUpperCase() === 'REGISTER' || item.type.toUpperCase() === 'FOLDER') {
                // show files in register/folder
                this.gridItems = item.children;
                this.setBreadcrumb('APPEND', [item]);
                this.setPathIndexUrl('APPEND', [item.id]);

                // reset doc viewer & quick search
                this.setDocViewerSrc();
                this.clearQuickSearch();

                this.highlightFirstDocument(item.children);
            } else {
                // preview file in doc viewer
                this.setDocViewerSrc(item.id);
            }
        },

        getFilesFromECM() {
            return new Promise((resolve, reject) => {
                this.$store.dispatch('projects/loadFilesByProject', { projectId: this.projectId })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getProjectCollaborators() {
            this.$store.dispatch('projects/loadCollaboratorsByProject', { projectId: this.projectId });
        },

        getNodeById(node, id) {
            if (node.id === id) {
                return node;
            } else if (node.children != null) {
                var foundNode = null;
                for(let i = 0; foundNode == null && i < node.children.length; i++) {
                    foundNode = this.getNodeById(node.children[i], id);
                }
                return foundNode;
            }
            return null;
        },

        getFirstDocNode(nodes) {
            for (let i = 0; i <= nodes.length - 1; i++) {
                let node = nodes[i];
                if (node.type && node.type.toUpperCase() === 'DOCUMENT') {
                    return node;
                }
            }
            return null;
        },

        openFileUploadDialog(file, isRenameMode, isReplaceMode) {

            if(isRenameMode || isReplaceMode) {
                this.fileRevisionInfo = undefined;
                projectsApi.getFileRevisionInfo(this.projectId, file)
                    .then(res => this.fileRevisionInfo = res.data);
            }
            this.isUploadModalInEditMode = isRenameMode || isReplaceMode;
            this.$root.$emit('bv::show::modal', 'dlgUploadFile', file, isRenameMode, isReplaceMode);
        },

        setDocViewerSrc(itemId) {
            if (!itemId) {
                this.iframeViewerUrl = undefined;
            }

            if (process.env.NODE_ENV.toUpperCase() === 'DEVELOPMENT') {
                this.iframeViewerUrl = this.globalConfig.proxyUrl + '/viewer/viewer.html?osid=' + itemId;
            } else {
                projectsApi.checkFilePreview(itemId, this.globalConfig.viewUrl)
                    .then(() => this.iframeViewerUrl = `${this.globalConfig.viewUrl}/dashlets/pdfview/web/viewer.html?osid=${itemId}`);
            }
        },

        highlightNodeById(id, isRefresh) {
            if (!isRefresh) {
                this.setDocViewerSrc(id);
            } else {
                if (this.$refs.iframeViewerRef.src) this.$refs.iframeViewerRef.src = this.$refs.iframeViewerRef.src;
            }
            
            setTimeout(() => {
                let rowIndex = null;
                this.gridApi.forEachNode(node => {
                    if (node.id === id) {
                        rowIndex = node.rowIndex;
                        this.gridApi.setFocusedCell(rowIndex);
                    }
                });
                
                if (rowIndex) {
                    this.gridApi.ensureIndexVisible(rowIndex, 'middle');
                }
            }, 100);
        },

        highlightFirstDocument(nodeList) {
            setTimeout(() => {
                let firstDocNode = this.getFirstDocNode(nodeList);
                if (firstDocNode) {
                    this.highlightNodeById(firstDocNode.id, false);
                } else {
                    this.highlightNodeById(undefined, true);
                }
            }, 500);
        },

        // eslint-disable-next-line
        uploadSuccess(fileItem) {
            if (this.isUploadModalInEditMode) {
                let fileIndex = this.gridItems.findIndex(item => item.id === fileItem.id);
                this.gridItems.splice(fileIndex, 1, fileItem);
                setTimeout(() => {
                    let folders = this.gridItems.filter(item => item.type.toUpperCase() !== 'DOCUMENT');
                    //console.log('folder count', folders.length);
                    //console.log('fileIndex', fileIndex);
                    fileIndex === folders.length ? this.highlightNodeById(fileItem.id, true) : this.highlightNodeById(fileItem.id, false);
                    this.$refs.iframeViewerRef.src = this.$refs.iframeViewerRef.src;
                }, 500)
            } else {
                // add row => no need to do anything since Vuex
                // will automagically take care of row addition :)
                setTimeout(() => {
                    this.highlightNodeById(fileItem.id);
                }, 500)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    .files-table-header {
        .header-cell {
            font-weight: 600;
            font-style: italic;
        }
    }

    .file-item-wrapper{
        div{
            align-items: center;
            display: flex;
        }
    }

    .sticky-row{
        display: flex;
    }

    .preview-text {
        text-align: center;
        margin-top: 20px;
    }

    .upload-group-container {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
    }
</style>
